import React, { createContext, useReducer } from 'react';
import type { NotificationContextType } from './types';
import { useToast } from '../ToastContext';
import { notificationReducer } from './reducer';
import { initialState, type NotificationProviderProps } from './types';
import { useUserSession } from './useUserSession';
import { usePendingOrders } from './usePendingOrders';
import { useLoadNotifications } from './useLoadNotifications';
import { useInitialLoad } from './useInitialLoad';
import { useNotificationSubscriptions } from './useNotificationSubscriptions';
import { useOrderSubscriptions } from './useOrderSubscriptions';
import { useLpClickSound } from './useLpClickSound';
import { useNotificationActions } from './useNotificationActions';

// Create context
export const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

/**
 * NotificationProvider component
 * Wrap your app dengan ini untuk mengakses notification context
 */
export function NotificationProvider({ children }: NotificationProviderProps) {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const { showToast } = useToast();
  const session = useUserSession();
  const pending = usePendingOrders();
  const { loadNotifications } = useLoadNotifications(dispatch, session);

  useNotificationSubscriptions(dispatch, session, showToast);
  useInitialLoad(session, loadNotifications);
  useOrderSubscriptions(session, pending);
  // Suara klik CTA Landing Page (semua user login, suara saja).
  useLpClickSound(session.user?.id);

  const { markAsRead, markAllAsRead, deleteNotification, addNotification } = useNotificationActions(state, dispatch);

  const value: NotificationContextType = {
    notifications: state.notifications,
    unreadCount: state.unreadCount,
    isLoading: state.isLoading,
    error: state.error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    addNotification,
    pendingNewOrders: pending.pendingNewOrders,
    showNewOrdersBanner: pending.showNewOrdersBanner,
    setShowNewOrdersBanner: pending.setShowNewOrdersBanner,
    clearPendingNewOrders: pending.clearPendingNewOrders,
    addPendingNewOrders: pending.addPendingNewOrders,
    updatePendingOrder: pending.updatePendingOrder,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

/**
 * Hook untuk menggunakan NotificationContext
 */
export function useNotificationContext() {
  const context = React.useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotificationContext must be used within NotificationProvider');
  }
  return context;
}
